import { z } from 'zod';
import { networkSchematicSchema } from './network-schematic.js';
import { indoorFixtureSchema, outdoorElementSchema, otherFixtureSchema, uncheckedFixtureSchema, } from './survey-matrix.js';
import { presenceAssessmentSchema } from './survey-v1.js';
/**
 * M16 — les cases « méthodes utilisées » de l'en-tête de la fiche, **v2 : quatre, plus cinq**.
 *
 * ⚠️ `COMCOM` n'en fait plus partie et n'y reviendra pas (#117) : ce n'est pas un moyen de
 * relevé, c'est la **communauté de communes** qui commande le contrôle. La fiche terrain la
 * nomme en clair (`COMCOM :`) ; l'inventaire du 05/08 avait lu la ligne 1 du classeur comme
 * cinq cases à cocher, la première étant un libellé suivi d'une valeur. Une case cochée, elle,
 * ne migre nulle part (voir {@link upgradeSurvey}).
 *
 * ⚠️ Ce que la ligne `COMCOM :` porte est la **réf. GHP** de la demande (#173) : l'écran la
 * montre sous ce nom, plus personne ne la saisit.
 *
 * Des **constantes**, pas une liste de `vocabulary` : elles ne se renomment pas depuis le
 * back-office, elles décrivent l'outillage de l'équipe.
 */
export const SURVEY_METHODS = ['PHOTOS', 'CAMERA', 'TOPO', 'FUMEE'];
/**
 * Le type de résidence : principale, secondaire ou locative (Sentry TERRAIN-9, GHP le 29/10) —
 * **pas** le type de bâti, que la demande porte déjà (`BUILDING_TYPES`). La liste de #118 (Étage,
 * Maison, Commerce…) en était un doublon.
 *
 * ⚠️ **Le champ reste ouvert malgré cette liste** (voir `dwelling.residenceType`) : une fiche
 * relevée avec l'ancienne liste, ou une saisie libre de la v1 (« principale »), doit se relire sans
 * rien perdre (M21). La liste dit ce que l'écran propose, pas ce que la base tolère.
 */
export const RESIDENCE_TYPES = ['primary', 'secondary', 'rental'];
/** La valeur relue est-elle de la liste, ou une valeur d'avant ? */
export function isResidenceType(value) {
    return RESIDENCE_TYPES.includes(value);
}
/**
 * Les types de résidence tels qu'ils s'impriment.
 *
 * ⚠️ Des libellés imprimés (troisième exception du glossaire), et **dans le domaine parce qu'ils ont
 * deux lecteurs qui ne peuvent pas s'importer l'un l'autre** : l'éditeur du bureau, et le rapport
 * PDF que l'API compose.
 */
export const RESIDENCE_TYPE_LABELS = {
    primary: 'Résidence principale',
    secondary: 'Résidence secondaire',
    rental: 'Locative',
};
/** Les mots de l'ancienne liste (#118), pour relire une fiche relevée avant TERRAIN-9. */
const FORMER_RESIDENCE_TYPE_LABELS = {
    floor: 'Étage',
    house: 'Maison',
    business: 'Commerce',
    apartment_building: 'Immeuble',
    professional: 'Professionnel',
    industrial: 'Industriel',
};
/**
 * Le type de résidence tel qu'il s'imprime. ⚠️ La valeur brute quand aucune liste ne la connaît :
 * le champ était libre en v1, et imprimer « — » sur une fiche qui porte « principale » ferait croire
 * à un relevé incomplet (M21).
 */
export function residenceTypeLabel(residenceType) {
    if (residenceType == null || residenceType === '')
        return null;
    return isResidenceType(residenceType)
        ? RESIDENCE_TYPE_LABELS[residenceType]
        : (FORMER_RESIDENCE_TYPE_LABELS[residenceType] ?? residenceType);
}
/**
 * La présence à l'année telle qu'elle s'imprime — « 9 mois par an » (Sentry TERRAIN-A) —, ou
 * « 8 an(s) 3 mois » pour une fiche relevée avec les deux cases d'avant, ou `null`.
 */
export function presenceLabel(dwelling) {
    if (dwelling?.presenceMonthsPerYear != null) {
        return `${String(dwelling.presenceMonthsPerYear)} mois par an`;
    }
    if (dwelling?.presenceYears == null && dwelling?.presenceMonths == null)
        return null;
    return `${String(dwelling.presenceYears ?? 0)} an(s) ${String(dwelling.presenceMonths ?? 0)} mois`;
}
/** L'avertissement d'une présence au-delà de 12 mois par an, ou `null` (M21 : rien n'est refusé). */
export function presenceMonthsWarning(months) {
    return months != null && months > 12 ? 'Une année compte 12 mois.' : null;
}
/**
 * #258 — les trois choix du bloc « Caractéristiques et contexte du terrain », **dans les mots de la
 * fiche et du rapport validé** (« Raccordement au réseau AEP : Oui », « Séparatif »).
 *
 * ⚠️ **Les champs restent des chaînes**, pour la raison de `residenceType` (M13) : aucune de ces
 * listes n'est confirmée par GHP, et les jeux de démonstration y ont déjà écrit du texte libre.
 * La liste dit ce que l'écran propose, pas ce que la base tolère.
 */
export const DRINKING_WATER_SUPPLY_LABELS = {
    connected: 'Oui',
    not_connected: 'Non',
};
export const PUBLIC_NETWORK_TYPE_LABELS = {
    separate: 'Séparatif',
    combined: 'Unitaire',
};
export const WASTEWATER_PRODUCTION_LABELS = {
    domestic: 'Domestique',
    professional: 'Professionnel',
    industrial: 'Industriel',
};
/**
 * Un choix du relevé tel qu'il s'imprime : son libellé, **la valeur brute** quand la liste ne la
 * connaît pas — imprimer « — » sur une saisie libre ferait croire à un oubli (M21) —, `null` quand
 * rien n'a été saisi.
 */
export function surveyChoiceLabel(labels, value) {
    if (value == null || value.trim() === '')
        return null;
    return Object.hasOwn(labels, value) ? (labels[value] ?? value) : value;
}
/**
 * #316 — les quatre « Remarques sur les installations » de GHP DIGIT. Des constantes, comme
 * `SURVEY_METHODS` : une remarque de plus est une ligne de fiche, pas une valeur à administrer.
 *
 * ⚠️ **La tablette ne les saisit plus** (#411, 09/10/2026) : les deux remarques « EU » se relèvent
 * en anomalies, le WC broyeur par sa ligne de la grille des points d'eau, la décompression dans les
 * observations de l'agent. Elles restent ici pour les relevés d'avant, que le bureau et le rapport
 * montrent encore.
 *
 * ⚠️ Ce que recouvre « Décompression » est posé à GHP (point 6 de la page de confirmation) : le
 * libellé reste celui de la fiche tant qu'il n'a pas répondu.
 */
export const INSTALLATION_REMARKS = [
    'decompression',
    'wastewater_through_septic_tank',
    'macerating_toilet',
    'wastewater_mixed_with_stormwater',
];
/** Verbatim du prototype validé (`Canvas controle`, frame 05) : la tablette, le bureau, le rapport. */
export const INSTALLATION_REMARK_LABELS = {
    decompression: 'Décompression',
    wastewater_through_septic_tank: 'Les EU transitent par une fosse',
    macerating_toilet: 'WC broyeur',
    wastewater_mixed_with_stormwater: 'Les EU sont mélangées aux EP avant rejet sur R. unitaire',
};
/**
 * Les remarques comme la fiche les montre : les quatre, dans son ordre, cochées ou non — puis, par
 * leur code, celles qu'une tablette plus récente aurait écrites (M21).
 */
export function installationRemarksOf(survey) {
    const checked = new Set(survey.installationRemarks ?? []);
    const known = new Set(INSTALLATION_REMARKS);
    return [
        ...INSTALLATION_REMARKS.map((code) => ({
            code,
            label: INSTALLATION_REMARK_LABELS[code],
            checked: checked.has(code),
        })),
        ...[...checked]
            .filter((code) => !known.has(code))
            .map((code) => ({ code, label: code, checked: true })),
    ];
}
/** Un décompte : entier, jamais négatif. Nommé une fois, la fiche en porte sept. */
const count = z.number().int().min(0);
const presence = presenceAssessmentSchema.nullish();
/**
 * La **nature** d'un ouvrage : privé ou public. Portée par les servitudes EU depuis la v1, et
 * par les deux boîtes de raccordement depuis la v2 (#113) — la forme existait, elle n'avait pas
 * été appliquée à la boîte.
 */
export const ownershipKindSchema = z.enum(['private', 'public']);
/**
 * La nature d'une boîte de raccordement ou d'une servitude, dans les mots de la fiche (#113) — même
 * raison d'être ici que `RESIDENCE_TYPE_LABELS` : l'écran et le rapport l'impriment tous deux.
 */
export const OWNERSHIP_KIND_LABELS = {
    private: 'Privée',
    public: 'Publique',
};
const ownershipKind = ownershipKindSchema.nullish();
/**
 * Une boîte de raccordement : présente ou non, **privée ou publique**, et sa position en clair.
 * ⚠️ La nature est facultative comme tout le reste de la fiche : une boîte qu'on n'a pas su
 * qualifier s'enregistre et se signale, elle ne bloque pas la transmission (M21).
 */
export const connectionBoxSchema = z.looseObject({
    present: z.boolean().nullish(),
    kind: ownershipKind,
    position: z.string().nullish(),
});
const connectionBox = connectionBoxSchema.optional();
/**
 * Une ligne de « Eaux de pluie vers les eaux usées » (`stormwater.ts`) : **raccordée ou non** aux
 * eaux usées, et la surface qui s'y écoule en m². Une paire ne porte que sa surface, un côté que sa
 * case, une surface simple les deux. `areaNote` remplace la mesure par « pour mémoire » ou
 * « inconnue », comme GHP DIGIT.
 *
 * ⚠️ **`side` et `areaNote` sont des chaînes, pas des `z.enum`**, pour la raison de `methodsUsed` :
 * une valeur inconnue ferait jeter le **relevé entier**, pas la ligne.
 */
export const connectedSurfaceSchema = z.looseObject({
    side: z.string(),
    connected: z.boolean().nullish(),
    areaM2: z.number().nonnegative().nullish(),
    areaNote: z.string().nullish(),
});
/**
 * Les trois bâtiments d'une même parcelle que le contrôle couvre — la maison, l'annexe arrière
 * (AR) et l'annexe avant (AV) du prototype (`BATIMENTS`).
 *
 * ⚠️ **Ce n'est pas la question des logements en immeuble** laissée ouverte à GHP (Q2) : il
 * s'agit ici de bâtiments d'une même propriété, que l'agent contrôle au cours de la même visite
 * et qui partagent l'adresse. Un immeuble identifié par escalier ou étage reste à trancher.
 */
export const INSPECTED_BUILDINGS = ['main', 'rear', 'front'];
/** Verbatim du prototype — « AR » et « AV » sont les mots que l'équipe emploie entre elle. */
export const INSPECTED_BUILDING_LABELS = {
    main: 'Maison',
    rear: 'AR — bâtiment arrière',
    front: 'AV — bâtiment avant',
};
/**
 * M16 — le relevé de terrain, **version 2** : les quatre écarts remontés par le test terrain du
 * 25/08 (#136 → #117, #119, #113, #118).
 *
 * Ce que la v2 change, et **rien d'autre** :
 *
 * - `general.delegatingAuthority` — la ComCom, la **communauté de communes**, sortie des méthodes
 *   (#117) ;
 * - `dwelling.referenceComplement` — le `E1D` de la fiche, d'où le type de résidence se
 *   propose (#118) ;
 * - `kind` sur les deux boîtes de raccordement (#113).
 *
 * #119 ne touche pas à cette forme : « Éléments manquants » est un **constat négatif** — l'agent
 * a constaté qu'il n'y a rien —, donc exactement ce que `absent` dit déjà. Seul le libellé
 * français affiché change (confirmé par le développeur le 09/09/2026).
 *
 * **Rien n'est obligatoire, et c'est le cœur de la fiche.** Une tablette peut porter une version
 * plus récente que l'API le temps d'un déploiement : valider en `strict` reviendrait à **effacer
 * un relevé de terrain** parce qu'il contient un champ de trop. On garde tout, on signale, le
 * back-office tranche (M21). D'où `looseObject` à chaque niveau — s'arrêter au premier laisserait
 * les sections jeter en silence.
 *
 * ⚠️ La v1 n'est pas modifiée : un contrôle déjà transmis la porte, et `upgradeSurvey` est la
 * seule couture entre les deux.
 */
export const surveyV2Schema = z.looseObject({
    /**
     * Les moyens cochés. Toujours `z.array(z.string())` et **pas** `z.enum(SURVEY_METHODS)` : c'est
     * par ce schéma qu'un relevé v1 entre, `COMCOM` comprise, avant que {@link upgradeSurvey} ne la
     * retire. Refuser ici un code inconnu détruirait la fiche entière pour une case — le schéma
     * garde, la montée trie (M21).
     */
    methodsUsed: z.array(z.string()).optional(),
    /** Des codes d'{@link INSTALLATION_REMARKS} — des chaînes, pour la raison de `methodsUsed`. */
    installationRemarks: z.array(z.string()).optional(),
    /** Les « INFORMATIONS GENERALES » de la fiche papier, côté contrôle. */
    general: z
        .looseObject({
        /**
         * La **communauté de communes** — la ligne `COMCOM :` de la fiche papier, en texte libre
         * faute de référentiel des collectivités. Ni la réf. GHP ni le client donneur d'ordre :
         * #173 l'avait lue comme la réf. GHP, GHP a confirmé le 02/10/2026 qu'elle nomme la
         * communauté de communes. L'agent la saisit dans l'onglet Infos ; le rapport PDF ne
         * l'imprime plus depuis le 22/10/2026 (#582), le classeur de GHP la lit en case B1.
         */
        delegatingAuthority: z.string().nullish(),
    })
        .optional(),
    dwelling: z
        .looseObject({
        constructionYear: z.number().int().nullish(),
        connectedDwellingsCount: count.nullish(),
        mainRoomsCount: count.nullish(),
        occupantsCount: count.nullish(),
        /**
         * Un code de {@link RESIDENCE_TYPES} quand l'écran l'a proposé, **du texte libre sinon**
         * (M13, M21) : la v1 laissait saisir n'importe quoi, et la relecture d'une fiche déjà
         * transmise ne doit rien perdre.
         */
        residenceType: z.string().nullish(),
        /** Le complément de la référence — `E1D`, `M`, `C`, ou le `-` qui dit qu'il n'y en a pas. */
        referenceComplement: z.string().nullish(),
        /** Les mois de l'année où le logement est occupé, de 0 à 12 (Sentry TERRAIN-A). */
        presenceMonthsPerYear: count.nullish(),
        /** Les deux cases d'avant TERRAIN-A : plus saisies, encore relues (M21). */
        presenceYears: count.nullish(),
        presenceMonths: count.nullish(),
        /** L'activité d'un occupant professionnel — « restaurant », « garage » —, sans code APE (#316). */
        occupantActivity: z.string().nullish(),
    })
        .optional(),
    plot: z
        .looseObject({
        areaM2: z.number().nonnegative().nullish(),
        drinkingWaterSupply: z.string().nullish(),
        publicNetworkType: z.string().nullish(),
        wastewaterProduction: z.string().nullish(),
        well: z
            .looseObject({
            present: z.boolean().nullish(),
            uses: z.array(z.string()).optional(),
            declared: z.boolean().nullish(),
        })
            .optional(),
    })
        .optional(),
    /**
     * La grille des points d'eau de la fiche (#258) — `survey-matrix.ts`. **Pas de v3** : ces clés
     * sont optionnelles et rien ne les avait écrites avant elles (M16).
     */
    indoorFixtures: z.array(indoorFixtureSchema).optional(),
    uncheckedFixtures: z.array(uncheckedFixtureSchema).optional(),
    /** La ligne « Autre » de la grille (#583, M45) — un point d'eau que la liste n'a pas. */
    otherFixture: otherFixtureSchema.nullish(),
    connectionEquipment: z
        .looseObject({
        septicTank: presence,
        greaseTrap: presence,
        basement: z.looseObject({ present: z.boolean().nullish() }).optional(),
        wastewaterCheckValve: z
            .looseObject({ exists: z.boolean().nullish(), required: z.boolean().nullish() })
            .optional(),
        stormwaterCheckValve: z
            .looseObject({ exists: z.boolean().nullish(), required: z.boolean().nullish() })
            .optional(),
        wastewaterPump: z
            .looseObject({
            present: z.boolean().nullish(),
            extent: z.enum(['full', 'partial']).nullish(),
        })
            .optional(),
        stormwaterPump: z
            .looseObject({
            present: z.boolean().nullish(),
            extent: z.enum(['full', 'partial']).nullish(),
        })
            .optional(),
        wastewaterEasements: z
            .looseObject({
            present: z.boolean().nullish(),
            kind: ownershipKind,
        })
            .optional(),
        wastewaterConnectionBox: connectionBox,
        stormwaterConnectionBox: connectionBox,
        wastewaterTrap: z
            .looseObject({ present: z.boolean().nullish(), position: z.string().nullish() })
            .optional(),
    })
        .optional(),
    /**
     * M11 — le **schéma du réseau** dessiné par l'agent, en JSON, dans le relevé : personne ne
     * requête l'intérieur d'un dessin. Son pendant image (« Export JPEG » de la maquette) est
     * produit par la tablette et attaché comme média — les deux ne sont pas redondants, le JSON
     * reste éditable et l'image garantit l'archive.
     *
     * ⚠️ **Déclaré, et pas seulement toléré par le `looseObject`.** Une clé inconnue entrerait
     * telle quelle, sans qu'aucune forme ne soit vérifiée : un dessin corrompu par une écriture
     * partielle arriverait alors jusqu'au rapport. Le déclarer est ce qui fait remonter le seul
     * refus du modèle de dessin — une forme sans géométrie.
     */
    schematic: networkSchematicSchema.optional(),
    /**
     * Le **tableau des eaux pluviales** (#412) — `survey-matrix.ts`. **Pas de v3**, pour la raison
     * des points d'eau : la clé est optionnelle et rien ne l'avait écrite avant elle.
     */
    outdoorElements: z.array(outdoorElementSchema).optional(),
    /**
     * « Eaux de pluie vers les eaux usées » (`stormwater.ts`), côté par côté — la carte d'avant le
     * tableau des eaux pluviales (#412). Plus aucun écran ne l'écrit ; un relevé qui la porte garde
     * sa lecture et sa conclusion.
     *
     * ⚠️ **Le total ne se stocke pas**, ni les anomalies qui en découlent : les deux se recalculent
     * sur les lignes ({@link connectedSurfaceSchema}), sans quoi ils cesseraient de dire ce que les
     * lignes disent dès qu'une case change.
     */
    connectedSurfaces: z.array(connectedSurfaceSchema).optional(),
    /**
     * **Où partent les eaux pluviales** — des codes de `STORMWATER_OUTLETS` (les six cases de GHP
     * DIGIT), en chaînes pour la raison de `methodsUsed`. `stormwaterUndetermined` : « toutes ou
     * partie des EP : destination indéterminée ». ⚠️ **La tablette ne les saisit plus** (TERRAIN-E) :
     * le tableau dit l'exutoire, et « Exutoire inconnu » y appelle le NOTA. Les clés restent pour
     * les relevés d'avant.
     */
    stormwaterOutlets: z.array(z.string()).optional(),
    stormwaterUndetermined: z.boolean().nullish(),
    /**
     * Le **débit du drain**, en litres par seconde — mesuré sur place, repris dans les données du
     * contrôle et le rapport final.
     *
     * ⚠️ L'unité est dans le nom, comme `areaM2` : un débit relu en m³/h à l'autre bout ne
     * produirait aucune erreur, juste un rapport faux d'un facteur 3600.
     */
    drainFlowRateLps: z.number().nonnegative().nullish(),
    /**
     * Les **bâtiments contrôlés** de la parcelle — des codes d'{@link INSPECTED_BUILDINGS}.
     *
     * `z.array(z.string())` et non `z.enum`, pour la raison de `methodsUsed` : un quatrième
     * bâtiment ajouté côté tablette ferait sinon jeter le relevé entier chez une API en retard
     * d'un déploiement.
     */
    inspectedBuildings: z.array(z.string()).optional(),
});
const yesNo = (value) => value == null ? null : value ? 'Oui' : 'Non';
/**
 * **Les équipements du raccordement dans les mots du rapport** (#258) — un clapet, un relevage, une
 * présence qualifiée. Dans le domaine pour la raison de `RESIDENCE_TYPE_LABELS` : le rapport les
 * imprime et le bureau les affiche, deux lecteurs qui ne peuvent pas s'importer l'un l'autre.
 *
 * `null` quand rien n'est relevé : chaque lecteur écrit son propre vide (« Non renseigné » au
 * rapport, « — » au bureau).
 */
export function checkValveSummary(valve) {
    if (valve?.exists == null && valve?.required == null)
        return null;
    // Une moitié relevée sans l'autre dit laquelle manque, dans les mots du rapport.
    return `Existant : ${yesNo(valve.exists) ?? 'Non renseigné'} · Nécessaire : ${yesNo(valve.required) ?? 'Non renseigné'}`;
}
/** Un relevage : « Oui (total) », « Oui (partiel) », « Oui », « Non ». */
export function pumpSummary(pump) {
    if (pump?.present == null)
        return null;
    if (!pump.present)
        return 'Non';
    return pump.extent === 'full'
        ? 'Oui (total)'
        : pump.extent === 'partial'
            ? 'Oui (partiel)'
            : 'Oui';
}
/** Une présence, sa nature et sa position : « Oui (Privée, jardin) », « Non ». */
export function presenceSummary(item) {
    if (item.present == null)
        return null;
    if (!item.present)
        return 'Non';
    const details = [
        item.kind == null ? null : OWNERSHIP_KIND_LABELS[item.kind],
        item.position?.trim() || null,
    ].filter((part) => part !== null);
    return details.length > 0 ? `Oui (${details.join(', ')})` : 'Oui';
}
/**
 * La version du schéma qui vient d'écrire un relevé — c'est elle que `Inspection.survey_version`
 * stocke, et celle qu'une tablette à jour annonce (`Proposal.payload_version`, lui, stocke
 * `PROPOSAL_PAYLOAD_VERSION` : l'enveloppe et le relevé versionnent chacun leur forme).
 */
export const SURVEY_VERSION = 2;
/** La case que la v1 cochait à tort — la seule valeur que la montée retire (#117). */
const COMCOM_METHOD_V1 = 'COMCOM';
/**
 * **La montée de version, appliquée à la lecture** (M16) — une fonction pure, le seul endroit où
 * un relevé v1 devient un relevé v2.
 *
 * La v2 n'ajoute que des champs : la conversion est structurelle, et le `parse` est ce qui
 * **re-type** la fiche. Il n'en retire qu'une valeur, dite juste en dessous.
 *
 * ⚠️ Un `COMCOM` coché en v1 **est retiré de `methodsUsed`**, et c'est le seul champ que la
 * montée ne rend pas tel quel. Ce n'est pas la **forme** du champ qui a changé, c'est sa
 * **nature** : on avait mal compris ce que la ComCom était (#117). Une case cochée ne nomme ni
 * une communauté de communes ni un identifiant — il n'y a donc rien à porter dans
 * `general.delegatingAuthority`, et rien à conserver non plus : c'est la trace d'une lecture
 * fausse, pas une donnée de terrain. La garder afficherait au bureau une puce `COMCOM` sous
 * « Méthodes utilisées » qui n'informe personne et que personne ne peut retirer.
 *
 * ⚠️ **`COMCOM` et elle seule.** `methodsUsed` reste volontairement permissif : jeter tout ce que
 * `SURVEY_METHODS` ignore serait une autre décision, qui n'est pas prise ici.
 *
 * ⚠️ **`null` quand la v1 ne peut pas devenir une v2, et surtout pas une exception.** Les deux
 * schémas sont `loose` : une v1 a pu ranger `kind: 'mixte'` sur une boîte — la clé n'existait pas
 * en v1, le passthrough l'a laissée entrer telle quelle — et la v2, elle, la connaît et la refuse.
 * Lever ici ferait tomber la **demande entière** en 500 chez l'appelant, donc emporterait les
 * propositions voisines qui, elles, se lisent très bien. Rendre `null` laisse une fiche illisible
 * être illisible **seule** : c'est l'écart `total`/`items` que la lecture existe pour dire (M21).
 */
export function upgradeSurvey(survey) {
    const upgraded = surveyV2Schema.safeParse(survey);
    if (!upgraded.success)
        return null;
    const { methodsUsed } = upgraded.data;
    if (methodsUsed === undefined)
        return upgraded.data;
    return {
        ...upgraded.data,
        methodsUsed: methodsUsed.filter((method) => method !== COMCOM_METHOD_V1),
    };
}
//# sourceMappingURL=survey-v2.js.map